'use client';

import { useState } from 'react';
import dynamic from 'next/dynamic';
import { useSearch } from '@/context/listing-search-provider';
import { ListingsFacets } from '@/utils/platform-api/__generated__/graphql';
import clsx from 'clsx';
import { ClientBrandConfig } from 'types/brand-config';

const FilterPopover = dynamic(
  () => import('@/components/filter/filter-popover'),
  { ssr: false }
);

const FiltersIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    width="21"
    height="18"
    viewBox="0 0 21 18"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <rect x="10" y="3" width="11" height="2" rx="1" fill="currentColor" />
    <rect y="3" width="6" height="2" rx="1" fill="currentColor" />
    <circle cx="8" cy="4" r="3" stroke="currentColor" strokeWidth="2" />
    <rect
      x="21"
      y="15"
      width="6"
      height="2"
      rx="1"
      transform="rotate(-180 21 15)"
      fill="currentColor"
    />
    <rect
      x="11"
      y="15"
      width="11"
      height="2"
      rx="1"
      transform="rotate(-180 11 15)"
      fill="currentColor"
    />
    <circle
      cx="13"
      cy="14"
      r="3"
      transform="rotate(-180 13 14)"
      stroke="currentColor"
      strokeWidth="2"
    />
  </svg>
);

type FiltersButtonVariant = 'icon' | 'text';

type FiltersButtonProps = {
  brand: ClientBrandConfig;
  submitPath?: string;
  listingFacets?: ListingsFacets;
  className?: string;
  variant?: FiltersButtonVariant;
  lockedFilterKeys?: SearchFiltersValues[];
};

export const FiltersButton = ({
  brand,
  submitPath,
  listingFacets,
  className,
  variant = 'icon',
  lockedFilterKeys,
}: FiltersButtonProps) => {
  const filterCategories = brand.filterCategories;
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const { totalAppliedFilters } = useSearch();

  if (!filterCategories) {
    return null;
  }

  return (
    <>
      <div className={clsx('relative shrink-0', variant === 'icon' && className)}>
        {totalAppliedFilters > 0 && (
          <div className="absolute -right-1 -top-1 z-10 flex h-5 w-5 items-center justify-center rounded-full bg-secondary-main">
            <span className="caps-lg text-center text-[0.625rem] leading-none text-secondary-contrastText">
              {totalAppliedFilters}
            </span>
          </div>
        )}
        {variant === 'text' ? (
          <button
            type="button"
            aria-label="Filters"
            onClick={() => setIsFilterOpen(true)}
            className={clsx(
              'flex items-center gap-1.5 p-0 font-sans text-base font-medium text-primary-main hover:text-primary-light',
              className
            )}
          >
            <FiltersIcon className="size-[1.125rem] shrink-0" />
            More Filters
          </button>
        ) : (
          <button
            type="button"
            aria-label="Filters"
            onClick={() => setIsFilterOpen(true)}
            className={clsx(
              'flex h-full min-h-12 items-center justify-center px-3 text-primary-main hover:text-primary-light',
              className
            )}
          >
            <FiltersIcon className="mt-0.5" />
          </button>
        )}
      </div>
      <FilterPopover
        listingFacets={listingFacets}
        filterCategories={filterCategories}
        open={isFilterOpen}
        setOpen={setIsFilterOpen}
        submitPath={submitPath}
        lockedFilterKeys={lockedFilterKeys}
      />
    </>
  );
};
